import popupStyles from "./planChangeImpactConfirmation.module.css";
import { IPopupWithoutProps } from "../../../types/types";
import venchyImg from "../../../images/questionnaire_doctor.png";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { ResourceCapsImpactResponse, SeatLossLine } from "../../../services/api/resourceCaps.api";
import { renderResourceImpactLine } from "../resourceCapImpactLine";
import { formatDateInIsrael } from "../../../utils/utils";

interface PlanChangeImpactConfirmationProps extends IPopupWithoutProps {
  onConfirm: () => void;
  planName: string;
  impact?: ResourceCapsImpactResponse;
  isLoadingImpact: boolean;
}

interface SeatLossSection {
  testId: string;
  line: SeatLossLine;
  lineKey: string;
}

function renderSeatLossLine({ testId, line, lineKey }: SeatLossSection) {
  // seats: 0 means nothing ends for this resource — ignore the rest of the line (backend contract).
  if (line.seats === 0) {
    return null;
  }

  // Past-due packs end on the next nightly run ("immediately"); the rest end at endsAt. Two
  // sentences, because with one pack of each a single "when" is wrong for one of them.
  const seatsEndingLater = line.seats - line.seatsEndingImmediately;
  const showImmediately = line.seatsEndingImmediately > 0;
  const showDated = seatsEndingLater > 0 && !!line.endsAt;

  // null, not an empty fragment, so the section (and its intro) is not rendered with no lines under it.
  if (!showImmediately && !showDated) {
    return null;
  }

  return (
    <>
      {showImmediately && (
        <p className={popupStyles.popup__text} data-testid={`${testId}-immediately`}>
          {t(lineKey, { count: line.seatsEndingImmediately, when: t("SEAT_PACKS_ENDING_IMMEDIATELY") })}
        </p>
      )}
      {showDated && line.endsAt && (
        <p className={popupStyles.popup__text} data-testid={`${testId}-dated`}>
          {t(lineKey, {
            count: seatsEndingLater,
            when: t("SEAT_PACKS_ENDING_ON", { date: formatDateInIsrael(line.endsAt) }),
          })}
        </p>
      )}
    </>
  );
}

interface DeactivationLine {
  testId: string;
  count: number;
  lineKey: string;
}

// The plain "N users will be deactivated" sentence, so the admin does not have to subtract the
// limit from the current count in their head.
function renderDeactivationLine({ testId, count, lineKey }: DeactivationLine) {
  if (count <= 0) {
    return null;
  }

  return (
    <p className={popupStyles.popup__text} data-testid={testId}>
      {t(lineKey, { count })}
    </p>
  );
}

function PlanChangeImpactConfirmation({
  onClose,
  onConfirm,
  planName,
  impact,
  isLoadingImpact,
}: PlanChangeImpactConfirmationProps) {
  const therapistsBlock = impact && renderResourceImpactLine({
    testId: "impact-block-therapists",
    resource: impact.therapists,
    summaryKey: "PLAN_CHANGE_IMPACT_SUMMARY_THERAPISTS",
  });
  const patientsBlock = impact && renderResourceImpactLine({
    testId: "impact-block-patients",
    resource: impact.patients,
    summaryKey: "PLAN_CHANGE_IMPACT_SUMMARY_PATIENTS",
  });
  const hasImpact = !!(therapistsBlock || patientsBlock);
  const therapistsDeactivatedNow = impact && renderDeactivationLine({
    testId: "deactivate-now-therapists",
    count: impact.therapists.wouldDeactivate,
    lineKey: "PLAN_CHANGE_IMPACT_DEACTIVATE_NOW_THERAPISTS",
  });
  const patientsDeactivatedNow = impact && renderDeactivationLine({
    testId: "deactivate-now-patients",
    count: impact.patients.wouldDeactivate,
    lineKey: "PLAN_CHANGE_IMPACT_DEACTIVATE_NOW_PATIENTS",
  });

  // A second, separate paragraph block: the lines above say what happens when you switch (today —
  // the plan's limits bite immediately), this says what happens when your purchased seat packs end
  // (later, at each pack's own boundary). null unless the target is a free plan and the company has
  // live packs — never rendered as a zeroed "you lose 0 seats" block.
  const seatPacksEnding = impact?.seatPacksEnding;
  const seatLossTherapistsLine = seatPacksEnding && renderSeatLossLine({
    testId: "seat-loss-line-therapists",
    line: seatPacksEnding.therapists,
    lineKey: "SEAT_PACKS_ENDING_LINE_THERAPISTS",
  });
  const seatLossPatientsLine = seatPacksEnding && renderSeatLossLine({
    testId: "seat-loss-line-patients",
    line: seatPacksEnding.patients,
    lineKey: "SEAT_PACKS_ENDING_LINE_PATIENTS",
  });
  const hasSeatLoss = !!(seatLossTherapistsLine || seatLossPatientsLine);

  // projectedWouldDeactivate is the total once the packs are gone, counted on today's headcount, so
  // it already includes the users deactivated at the switch. Only the difference is "more".
  const therapistsDeactivatedLater = impact && seatPacksEnding && renderDeactivationLine({
    testId: "deactivate-later-therapists",
    count: seatPacksEnding.therapists.projectedWouldDeactivate - impact.therapists.wouldDeactivate,
    lineKey: "SEAT_PACKS_ENDING_DEACTIVATE_THERAPISTS",
  });
  const patientsDeactivatedLater = impact && seatPacksEnding && renderDeactivationLine({
    testId: "deactivate-later-patients",
    count: seatPacksEnding.patients.projectedWouldDeactivate - impact.patients.wouldDeactivate,
    lineKey: "SEAT_PACKS_ENDING_DEACTIVATE_PATIENTS",
  });

  return (
    <article className={popupStyles.popup} data-testid="plan-change-impact-popup">
      <img className={popupStyles.popup__img} src={venchyImg} alt={t('VENCHI')} />
      <h2 className={popupStyles.popup__title}>{t('PLAN_CHANGE_IMPACT_TITLE', { planName })}</h2>

      {isLoadingImpact ? (
        <p className={popupStyles.popup__text}>{t('LOADING')}</p>
      ) : (
        <>
          {hasImpact ? (
            <>
              {therapistsBlock}
              {therapistsDeactivatedNow}
              {patientsBlock}
              {patientsDeactivatedNow}
              <p className={popupStyles.popup__warningDetail} role="alert">
                {t('PLAN_CHANGE_IMPACT_DEACTIVATION_CHOICE')}
              </p>
            </>
          ) : (
            <p className={popupStyles.popup__text}>{t('PLAN_CHANGE_IMPACT_NO_IMPACT')}</p>
          )}

          {hasSeatLoss && (
            <section className={popupStyles.popup__seatPacksSection} data-testid="seat-loss-section">
              <p className={popupStyles.popup__text}>{t('SEAT_PACKS_ENDING_INTRO')}</p>
              {seatLossTherapistsLine}
              {seatLossPatientsLine}
              {therapistsDeactivatedLater}
              {patientsDeactivatedLater}
              <p className={popupStyles.popup__mutedNote}>{t('SEAT_PACKS_ENDING_EMAIL_NOTE')}</p>
            </section>
          )}
        </>
      )}

      <div className={popupStyles.popup__buttons}>
        <button
          className={`button ${popupStyles.popup__confirmBtn} hover`}
          data-testid="confirm-plan-change-button"
          onClick={onConfirm}
          disabled={isLoadingImpact}
        >
          {t('CONFIRM')}
        </button>
        <button
          className={`button ${popupStyles.popup__cancelBtn} hover`}
          data-testid="cancel-plan-change-button"
          onClick={onClose}
        >
          {t('CANCEL')}
        </button>
      </div>
    </article>
  );
}

export default PlanChangeImpactConfirmation;
